import { Flame } from 'lucide-react'
import type { Goal, GoalBoardCard } from '../../services/goalService'
import { Timestamp } from '../ui/Timestamp'
import { Last14Practice } from '../skills/SkillKeepingUp'
import { keepingUpWords } from '../../utils/keepingUpWords'
import { GoalProgressEditor } from './inline/GoalInlineEditors'

/**
 * How a goal has been going, in one glance (Goals UX pass 2026-09-06): how
 * far along, the last fourteen days as dots (a day counts when a task got
 * finished, a habit got done, or a skill under it was practised), days with
 * something done since it began, the last time, and what is under it. Tone:
 * what happened, never a scolding.
 *
 * 2026-09-12: the percentage is edited here (it was a separate "Progress"
 * panel saying the same number a third time), and the journey's count lives
 * on the Journey heading only.
 */
export function GoalKeepingUp({ goal, card, headingClass, live = false, onChanged }: { goal: Goal; card: GoalBoardCard | null; headingClass: string; live?: boolean; onChanged?: (goal: Goal) => void }) {
  const pct = Math.round(Number(goal.progress || 0))
  const habits = card?.items.habits.filter((h) => h.live).length || 0
  const skills = card?.items.skills.length || 0
  const openTasks = card?.open_tasks || 0
  const under: string[] = []
  if (openTasks) under.push(`${openTasks} task${openTasks === 1 ? '' : 's'} open`)
  if (habits) under.push(`${habits} habit${habits === 1 ? '' : 's'} going`)
  if (skills) under.push(`${skills} skill${skills === 1 ? '' : 's'}`)
  return (
    <section data-testid="goal-keeping-up">
      <h3 className={headingClass}><Flame className="h-3.5 w-3.5 text-brand" /> Keeping up</h3>
      <div className="mt-2 flex items-center gap-3" data-testid="goal-progress-bar">
        <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-soft"><div className="h-full rounded-full bg-brand transition-all" style={{ width: `${Math.max(0, Math.min(100, pct))}%` }} /></div>
        <span className="text-app-meta font-medium text-primary">
          {live && onChanged ? <GoalProgressEditor goal={goal} onChanged={onChanged} /> : `${pct}%`}
        </span>
      </div>
      {card && (
        <>
          <div className="mt-3"><Last14Practice days={card.last_14} /></div>
          <p className="mt-2 text-sm text-primary" data-testid="goal-keeping-up-words">
            {keepingUpWords(card, { quiet: 'A while since something got done under it', none: 'Nothing under way yet' })}
          </p>
          <dl className="mt-1 space-y-0.5 text-app-meta text-secondary">
            {card.last_at && <div>Last <Timestamp value={card.last_at} mode="datetime" /></div>}
            {!card.last_at && card.last_date && <div>Last <Timestamp value={`${card.last_date}T12:00:00`} mode="date" /></div>}
            {under.length > 0 && <div>{under.join(' · ')}</div>}
          </dl>
        </>
      )}
    </section>
  )
}

export default GoalKeepingUp
